/**
 * §58 — is a hauler feature on, for the dispatcher console (DEC-366).
 *
 * Read from the profile's `features`. That field is ABSENT when the server could not read
 * the features, and the contract is "keep the last-known set" (never read absence as
 * "everything off"): this remembers the last map seen for the signed-in user IN THE ACTIVE
 * COMPANY (user + tenant), so a multi-company user never sees one company's features in
 * another (#877 review).
 *
 * useFeatureEnabled returns null while nothing is known: the profile has not loaded, or it
 * came without features and none are remembered. A gated surface never GUESSES: a map shown
 * "until we know" would cost a Mapbox load for a hauler without the feature, and a "not on
 * your plan" message would be wrong if the read merely failed. useFeatureState tells the two
 * unknowns apart, so a page can say "Loading…" or "couldn't check your plan" instead of
 * staying blank (#881 review), and while the features are unknown it asks for the profile
 * again every 30 s rather than waiting for the 5-minute refresh.
 */
import * as React from 'react';
import type { FeatureId, UserProfile } from '@lodex-pro/shared';
import { useUserProfile } from './use-user-timezone.js';

type FeatureMap = NonNullable<UserProfile['features']>;

/** ON / OFF once known; LOADING before the profile arrives; UNKNOWN when it came without features. */
export type FeatureState = 'ON' | 'OFF' | 'LOADING' | 'UNKNOWN';

const UNKNOWN_RETRY_MS = 30_000;

let lastKnown: { key: string; features: FeatureMap } | null = null;

/** Tests only. */
export function resetFeatureMemoryForTests(): void {
  lastKnown = null;
}

function memoryKey(profile: UserProfile): string {
  return `${profile.userId}/${profile.tenantId ?? ''}`;
}

export function useFeatureState(id: FeatureId): FeatureState {
  const { data, isError, refetch } = useUserProfile();
  const profile = data?.profile;
  let state: FeatureState;
  if (profile === undefined) {
    // #890 review: a profile request that FAILED with nothing loaded is not "loading" (that
    // would show Loading… for good): it is unknown, which shows "couldn't check your plan"
    // and keeps asking every 30 s.
    state = isError ? 'UNKNOWN' : 'LOADING';
  } else {
    const key = memoryKey(profile);
    if (profile.features !== undefined) {
      lastKnown = { key, features: profile.features };
      state = profile.features[id]?.enabled === true ? 'ON' : 'OFF';
    } else if (lastKnown !== null && lastKnown.key === key) {
      state = lastKnown.features[id]?.enabled === true ? 'ON' : 'OFF';
    } else {
      state = 'UNKNOWN';
    }
  }

  const unknown = state === 'UNKNOWN';
  React.useEffect(() => {
    if (!unknown) return undefined;
    // cancelRefetch false: every gated component runs this, and they must share one request.
    const again = (): void => void refetch({ cancelRefetch: false });
    again();
    const handle = setInterval(again, UNKNOWN_RETRY_MS);
    return () => clearInterval(handle);
  }, [unknown, refetch]);

  return state;
}

export function useFeatureEnabled(id: FeatureId): boolean | null {
  const state = useFeatureState(id);
  if (state === 'ON') return true;
  if (state === 'OFF') return false;
  return null;
}
